import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { getInvoice, updateInvoice, recordPayment, chargeCard, deleteInvoice, downloadSuperbill } from '../services/invoiceService';
import Badge from '../components/common/Badge';
import Spinner from '../components/common/Spinner';
import Modal from '../components/common/Modal';

const STATUS_VARIANTS = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  partially_paid: 'warning',
  overdue: 'error',
  cancelled: 'default',
};

const PAYMENT_METHODS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'check',         label: 'Check' },
  { value: 'credit_card',   label: 'Credit card' },
  { value: 'debit_card',    label: 'Debit card' },
  { value: 'ach',           label: 'ACH / Bank transfer' },
  { value: 'zelle',         label: 'Zelle' },
  { value: 'venmo',         label: 'Venmo' },
  { value: 'bank_transfer', label: 'Bank transfer (other)' },
  { value: 'other',         label: 'Other' },
];

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

const paymentSchema = z.object({
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  paymentDate: z.string().min(1, 'Required'),
  paymentMethod: z.string().optional().or(z.literal('')),
  referenceNumber: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
});

function PaymentForm({ balance, onSubmit, onCancel, isSubmitting }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: { amount: balance.toFixed(2), paymentDate: today, paymentMethod: '' },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Amount *</label>
          <input type="number" step={0.01} min={0.01} className="input" {...register('amount')} />
          {errors.amount && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.amount.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payment date *</label>
          <input type="date" className="input" {...register('paymentDate')} />
          {errors.paymentDate && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.paymentDate.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payment method</label>
          <select className="input" {...register('paymentMethod')}>
            <option value="">Select…</option>
            {PAYMENT_METHODS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Reference #</label>
          <input type="text" className="input" placeholder="Check #, transaction ID…" {...register('referenceNumber')} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
        <textarea className="input" rows={2} {...register('notes')} />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn btn-primary disabled:opacity-60">
          {isSubmitting ? 'Recording…' : 'Record payment'}
        </button>
      </div>
    </form>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCharging, setIsCharging] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoice(id),
  });

  const paymentMutation = useMutation({
    mutationFn: (formData) => recordPayment(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceSummary'] });
      setIsPaymentOpen(false);
      toast.success('Payment recorded');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to record payment'),
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus) => updateInvoice(id, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceSummary'] });
      toast.success('Invoice updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update invoice'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate('/billing');
      toast.success('Invoice deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete invoice'),
  });

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (isError) return <div className="text-center py-20 text-danger-600">Failed to load invoice.</div>;

  const { invoice } = data;
  const balance = parseFloat(invoice.total) - parseFloat(invoice.amountPaid);
  const canPay = !['paid', 'cancelled'].includes(invoice.status) && balance > 0;
  const canCancel = !['cancelled', 'paid'].includes(invoice.status);
  const canSend = invoice.status === 'draft';
  const canDelete = invoice.status === 'draft';
  const hasCardOnFile = Boolean(invoice.client?.cardLast4);

  const handleChargeCard = async () => {
    setIsCharging(true);
    try {
      await chargeCard(id);
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceSummary'] });
      toast.success('Card charged successfully');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to charge card';
      toast.error(msg);
    } finally {
      setIsCharging(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/billing" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{invoice.invoiceNumber}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge
                label={invoice.status.replace('_', ' ')}
                variant={STATUS_VARIANTS[invoice.status] || 'default'}
              />
              {invoice.client && (
                <span className="text-sm text-neutral-500 dark:text-neutral-400">
                  · {invoice.client.firstName} {invoice.client.lastName}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={async () => {
              setIsDownloading(true);
              try {
                await downloadSuperbill(id, invoice.invoiceNumber);
              } catch {
                toast.error('Failed to download superbill');
              } finally {
                setIsDownloading(false);
              }
            }}
            disabled={isDownloading}
            className="btn btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-60"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {isDownloading ? 'Generating…' : 'Superbill'}
          </button>
          {canPay && hasCardOnFile && (
            <button
              onClick={handleChargeCard}
              disabled={isCharging}
              className="btn btn-primary text-sm disabled:opacity-60"
              title={`Charge ${invoice.client?.cardBrand} ****${invoice.client?.cardLast4}`}
            >
              {isCharging ? 'Charging…' : `Charge card ****${invoice.client?.cardLast4}`}
            </button>
          )}
          {canPay && (
            <button onClick={() => setIsPaymentOpen(true)} className="btn btn-secondary text-sm">
              Record payment
            </button>
          )}
          {canSend && (
            <button
              onClick={() => statusMutation.mutate('sent')}
              disabled={statusMutation.isPending}
              className="btn btn-secondary text-sm"
            >
              Mark as sent
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => statusMutation.mutate('cancelled')}
              disabled={statusMutation.isPending}
              className="btn btn-secondary text-sm text-danger-600"
            >
              Cancel invoice
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setIsDeleteOpen(true)}
              className="btn btn-secondary text-sm text-danger-600"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Invoice details */}
      <div className="card mb-6">
        <div className="grid grid-cols-3 gap-6 text-sm">
          <div>
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Invoice date</p>
            <p className="text-neutral-900 dark:text-neutral-100">{format(parseISO(invoice.invoiceDate), 'MMMM d, yyyy')}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Due date</p>
            <p className="text-neutral-900 dark:text-neutral-100">{format(parseISO(invoice.dueDate), 'MMMM d, yyyy')}</p>
          </div>
          {invoice.notes && (
            <div>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-neutral-900 dark:text-neutral-100">{invoice.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Line items */}
      <div className="card mb-6 p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Description</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Qty</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Unit price</th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {(invoice.items || []).map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-3 text-neutral-700 dark:text-neutral-300">{item.description}</td>
                <td className="px-4 py-3 text-center text-neutral-600 dark:text-neutral-400">{item.quantity}</td>
                <td className="px-4 py-3 text-right text-neutral-600 dark:text-neutral-400">{fmt(item.unitPrice)}</td>
                <td className="px-6 py-3 text-right font-medium text-neutral-900 dark:text-neutral-100">{fmt(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 px-6 py-4 space-y-2">
          <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
            <span>Subtotal</span><span>{fmt(invoice.subtotal)}</span>
          </div>
          {parseFloat(invoice.tax) > 0 && (
            <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
              <span>Tax</span><span>{fmt(invoice.tax)}</span>
            </div>
          )}
          {parseFloat(invoice.discount) > 0 && (
            <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
              <span>Discount</span><span>-{fmt(invoice.discount)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-base border-t border-neutral-200 dark:border-neutral-700 pt-2">
            <span>Total</span><span>{fmt(invoice.total)}</span>
          </div>
          <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
            <span>Amount paid</span><span>{fmt(invoice.amountPaid)}</span>
          </div>
          <div className={`flex justify-between font-semibold text-sm ${balance > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
            <span>Balance due</span><span>{fmt(balance)}</span>
          </div>
        </div>
      </div>

      {/* Payment history */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="card">
          <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Payment History</h2>
          <div className="space-y-3">
            {invoice.payments.map((payment) => (
              <div key={payment.id} className="flex justify-between items-center text-sm">
                <div>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">{fmt(payment.amount)}</span>
                  {payment.paymentMethod && (
                    <span className="text-neutral-500 dark:text-neutral-400 ml-2">
                      · {PAYMENT_METHODS.find((m) => m.value === payment.paymentMethod)?.label ?? payment.paymentMethod}
                    </span>
                  )}
                  {payment.referenceNumber && (
                    <span className="text-neutral-400 dark:text-neutral-500 ml-2">#{payment.referenceNumber}</span>
                  )}
                </div>
                <span className="text-neutral-500 dark:text-neutral-400">
                  {format(parseISO(payment.paymentDate), 'MMM d, yyyy')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      <Modal isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)} title="Record Payment" size="md">
        <PaymentForm
          balance={balance}
          onSubmit={(data) => paymentMutation.mutate(data)}
          onCancel={() => setIsPaymentOpen(false)}
          isSubmitting={paymentMutation.isPending}
        />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} title="Delete invoice?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          This will permanently delete {invoice.invoiceNumber}. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setIsDeleteOpen(false)} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
